import { makeAutoObservable, runInAction } from 'mobx';

import { rejectExtraActivityAssignment } from 'common/activeElements/modals/ModalDeleteExtraActivity/rejectExtraActivityAssignment';
import { formatOverlappingActivities } from 'common/OverlappingActivitiesBanner/helpers/overlappingActivities';
import { WA_OVERLAPPING_ACTIVITIES_RESPONSE } from 'constants/activityTypes';
import { client } from 'contexts/ApolloClient/apolloClient';
import { standardError } from 'global/commonMessages';
import { notificationStore } from 'global/NotificationStore';
import { findRideDetailsMockById } from 'mocks/rideDetails';
import {
	blankWA,
	breakFullBatch,
	generateAssignmentsMapFromSequences,
	getMergeStrategyForSequence,
	getStationsForAssignment,
} from 'utils/assigmentsGenerator';
import { createAssignmentErrorNotification } from 'utils/assignmentCustomErrorGenerator';
import { handleError } from 'utils/error-parser';

import { actionLogStore } from './components/ActionLog/ActionLogStore';
import { ACTION_MARKERS } from './components/Assigment/const';
import { CREATE_WORK_ASSIGMENT } from './gql/createWorkAssignment';
import { FETCH_RIDE_BY_ID } from './gql/fetchRide';
import { UPDATE_WORK_ASSIGMENT_STATUS } from './gql/updateWorkAssigment';
import { insertVirtualStations } from './utils/insertVirtualStations';

class RideDetailsStore {
	activityType = null;
	details = null;
	stations = [];
	stationsData = {};
	workAssignments = {};
	workAssignmentsOrder = [];
	// Raw sequences kept alongside the processed `workAssignments` map because
	// downstream consumers (e.g. Breakdown details modal) need to resolve the
	// segment (from → to) at which the breakdown happened, and the Map-based
	// `workAssignments` no longer carries that ordering.
	workAssignmentSequences = [];
	vehicleAssignments = [];
	loading = true;
	rideId = '';
	workAssignmentInSelection = '';
	isInDriverSelection = false;
	reassignCrewMemberId = null;
	error = false;
	stationTimezone = null;
	selectedTimezone = null;
	batchControls = [];
	showAutobatchPopup = false;
	isDeletingExtraActivity = false;
	// null when no mock is active; otherwise the mock id from RIDE_DETAILS_MOCKS.
	activeMockId = null;

	constructor() {
		makeAutoObservable(this);
	}

	setActiveMockId = (mockId) => {
		runInAction(() => {
			this.activeMockId = mockId;
		});
		if (this.rideId) {
			this.fetchInitialData(this.rideId);
		}
	};

	fetchInitialData = async (rideId) => {
		this.loading = true;
		try {
			// Dev-only escape hatch: when a mock id is active, replace the
			// network response with the matching in-repo fixture so we can
			// exercise the virtual-station branch (and its variants) on
			// localhost without a matching backend record.
			const activeMock = this.activeMockId ? findRideDetailsMockById(this.activeMockId) : null;
			const rideResponse = activeMock
				? activeMock.payload
				: (
						await client.query({
							query: FETCH_RIDE_BY_ID,
							variables: { rideId },
							fetchPolicy: 'no-cache',
						})
					).data.getRideById;
			const {
				lineCode,
				tripNumber,
				id,
				stations: rawStations,
				departure,
				vehicleAssignments,
				activityTypeConfigs,
				workAssignmentSequences,
				runs,
				jobs,
			} = rideResponse;

			// Backend does not include mid-route breakdown locations in
			// `stations[]` — only inside the surrounding sequences. Splice
			// them in first so every downstream consumer (placement,
			// assignments map, stationsData) sees a complete route.
			const stations = insertVirtualStations({
				stations: rawStations,
				workAssignmentSequences,
			});

			const mappedAssignments = generateAssignmentsMapFromSequences(
				stations,
				workAssignmentSequences
			);

			runInAction(() => {
				this.details = {
					lineCode,
					tripNumber,
					id,
					stationFirst: stations[0].name,
					stationLast: stations[stations.length - 1].name,
					departure,
					firstStationTimezone: stations[0].timezone,
					runs,
					jobs,
					breakdown: workAssignmentSequences?.find((seq) => seq.breakdown)?.breakdown ?? null,
				};
				this.activityType = activityTypeConfigs.find((type) => type.limit > 0).activityType;
				this.stations = stations;
				this.stationsData = stations.reduce(
					(acc, station, index) => acc.set(station.id, { ...station, index }),
					new Map()
				);
				this.workAssignments = mappedAssignments;
				this.workAssignmentSequences = workAssignmentSequences ?? [];
				this.vehicleAssignments = vehicleAssignments;
				this.loading = false;
				this.rideId = rideId;
				this.stationTimezone = {
					name: 'Station time',
					value: stations[0].timezone,
				};
				this.selectedTimezone = {
					name: 'Station time',
					value: stations[0].timezone,
				};
			});

			actionLogStore.fetchActionLog(rideId);
			return true;
		} catch (error) {
			runInAction(() => {
				this.loading = false;
				this.error = true;
			});
			notificationStore.setNotification(standardError);
			return false;
		}
	};

	getAssignmentByID(id) {
		return this.workAssignments?.get(id) || blankWA;
	}

	initializeAssignmentState(assignmentStoreId) {
		runInAction(() => {
			this.workAssignmentInSelection = assignmentStoreId;
			this.isInDriverSelection = true;
		});
	}

	exitAssignmentState() {
		this.isInDriverSelection = false;
		this.reassignCrewMemberId = null;
	}

	clearReassignCrewMemberId() {
		this.reassignCrewMemberId = null;
	}

	createWorkAssignment = async (
		crewMemberId,
		{ overlappingActivityAction = null, delayMinutes = null } = {},
		reassing = false
	) => {
		if (!this.isInDriverSelection && !reassing) return;

		this.workAssignments.set(this.workAssignmentInSelection, {
			...this.workAssignments.get(this.workAssignmentInSelection),
			isLoading: true,
		});

		const { fromStationId, toStationId } = this.workAssignments.get(this.workAssignmentInSelection);

		const variables = {
			newAssignment: {
				rideId: this.rideId,
				fromStationId,
				toStationId,
				crewMemberId,
				activityType: this.activityType,
			},
			overlappingActivityAction,
			delayMinutes,
		};

		try {
			const response = await client.mutate({
				mutation: CREATE_WORK_ASSIGMENT,
				variables,
				fetchPolicy: 'no-cache',
			});

			if (Object.hasOwn(response.data.batchAssignCrewMemberV2, 'errors')) {
				const { errors } = response.data.batchAssignCrewMemberV2;

				handleError(errors);

				this.workAssignments.set(this.workAssignmentInSelection, {
					...this.workAssignments.get(this.workAssignmentInSelection),
					isLoading: false,
					isGlobalBatched: false,
				});
				return;
			}

			if (response.data.batchAssignCrewMemberV2.__typename === WA_OVERLAPPING_ACTIVITIES_RESPONSE) {
				const { overlappingActivities, minDelayMinutes, maxDelayMinutes } =
					response.data.batchAssignCrewMemberV2;

				this.workAssignments.set(this.workAssignmentInSelection, {
					...this.workAssignments.get(this.workAssignmentInSelection),
					isLoading: false,
				});

				return {
					kind: 'overlap',
					cannotCancel: overlappingActivities.some((a) => !a.isCancellable),
					overlappingActivities,
					minDelayMinutes,
					maxDelayMinutes,
				};
			}

			const { assignment, keysToDelete } = getMergeStrategyForSequence(
				response.data.batchAssignCrewMemberV2.workAssignmentSequences[0],
				this.stations
			);
			const autobatched = response.data?.batchAssignCrewMemberV2?.adjacentWorkAssignmentsAffected;

			runInAction(() => {
				keysToDelete.forEach((id) => this.workAssignments.delete(id));
				this.workAssignmentInSelection = '';
				this.workAssignments.set(assignment.storeId, {
					...assignment,
				});
				this.showAutobatchPopup = autobatched;
			});
		} catch (error) {
			notificationStore.setNotification(standardError);

			runInAction(() => {
				this.workAssignments.set(this.workAssignmentInSelection, {
					...this.workAssignments.get(this.workAssignmentInSelection),
					isLoading: false,
				});
			});
		} finally {
			actionLogStore.fetchActionLog(this.rideId);
		}
	};

	getStationById = (id) => {
		return this.stationsData.get(id);
	};

	mergeMutatedAssignmentSequence = (sequence) => {
		const { assignment, keysToDelete } = getMergeStrategyForSequence(sequence, this.stations);

		runInAction(() => {
			keysToDelete.forEach((id) => this.workAssignments.delete(id));
			this.workAssignments.set(assignment.storeId, {
				...assignment,
			});
		});

		actionLogStore.fetchActionLog(this.rideId);
	};

	refetchCurrentRide = async () => {
		if (!this.rideId) return false;

		return this.fetchInitialData(this.rideId);
	};

	rejectExtraActivityAssignment = async (workAssignmentKey) => {
		if (
			!workAssignmentKey?.id ||
			workAssignmentKey.version === undefined ||
			workAssignmentKey.version === null
		) {
			notificationStore.setNotification(standardError);
			return false;
		}

		runInAction(() => {
			this.isDeletingExtraActivity = true;
		});

		let didRefetchRide = false;

		try {
			const sequence = await rejectExtraActivityAssignment(workAssignmentKey);

			if (sequence) {
				didRefetchRide = await this.refetchCurrentRide();
				return true;
			}

			return false;
		} catch (error) {
			notificationStore.setNotification(standardError);
			return false;
		} finally {
			runInAction(() => {
				this.isDeletingExtraActivity = false;
			});
			if (!didRefetchRide) {
				actionLogStore.fetchActionLog(this.rideId);
			}
		}
	};

	updateAssignmentStatus = async (assignmentStoreId, updatedStatus) => {
		if (Object.hasOwn(ACTION_MARKERS, updatedStatus)) {
			this.hanldeAction(assignmentStoreId, updatedStatus);
			return;
		}
		this.workAssignments.set(assignmentStoreId, {
			...this.workAssignments.get(assignmentStoreId),
			isLoading: true,
		});

		const { fromStationId, toStationId, crewMember } = this.workAssignments.get(assignmentStoreId);

		const variables = {
			statusUpdate: {
				rideId: this.rideId,
				fromStationId,
				toStationId,
				crewMemberId: crewMember.id,
				activityType: this.activityType,
				status: updatedStatus,
			},
		};
		try {
			const response = await client.mutate({
				mutation: UPDATE_WORK_ASSIGMENT_STATUS,
				variables,
				fetchPolicy: 'no-cache',
			});
			if (Object.hasOwn(response.data.batchWorkAssignmentsStatusUpdateV2, 'errors')) {
				const { errors } = response.data.batchWorkAssignmentsStatusUpdateV2;

				const errorsMapped = errors
					.map(({ errors }) => [...errors])
					.reduce((errorArray, result) => [...result, ...errorArray], []);

				const errorsUnique = [...new Set(errorsMapped)];
				this.sendCustomizedErrorNotification(assignmentStoreId, errorsUnique);

				this.workAssignments.set(assignmentStoreId, {
					...this.workAssignments.get(assignmentStoreId),
					isLoading: false,
				});
				return;
			}

			if (
				Object.hasOwn(response.data.batchWorkAssignmentsStatusUpdateV2, 'overlappingActivities')
			) {
				const { overlappingActivities } = response.data.batchWorkAssignmentsStatusUpdate;
				const errorsMapped = formatOverlappingActivities(overlappingActivities);

				this.sendCustomizedErrorNotification(assignmentStoreId, errorsMapped);

				this.workAssignments.set(assignmentStoreId, {
					...this.workAssignments.get(assignmentStoreId),
					isLoading: false,
				});
				return;
			}

			const { assignment, keysToDelete } = getMergeStrategyForSequence(
				response.data.batchWorkAssignmentsStatusUpdateV2.workAssignmentSequences[0],
				this.stations
			);
			const autobatched = response.data?.batchAssignCrewMemberV2?.adjacentWorkAssignmentsAffected;

			runInAction(() => {
				keysToDelete.forEach((id) => this.workAssignments.delete(id));
				this.workAssignmentInSelection = '';
				this.workAssignments.set(assignment.storeId, {
					...assignment,
				});
				this.showAutobatchPopup = autobatched;
			});
		} catch (error) {
			notificationStore.setNotification(standardError);

			this.workAssignments.set(assignmentStoreId, {
				...this.workAssignments.get(assignmentStoreId),
				isLoading: false,
			});
		} finally {
			actionLogStore.fetchActionLog(this.rideId);
		}
	};

	hanldeAction = (assignmentStoreId, actionType) => {
		switch (actionType) {
			case ACTION_MARKERS.ASSIGN_ANOTHER:
				this.initializeAssignmentState(assignmentStoreId);
				return;
			case ACTION_MARKERS.REASSIGN:
				this.reassignCrewMemberId = this.workAssignments.get(assignmentStoreId).crewMember;
				this.initializeAssignmentState(assignmentStoreId);
				return;
			default:
				return;
		}
	};

	sendCustomizedErrorNotification(assignmentStoreId, errors) {
		const { fromStationId, toStationId } = this.workAssignments.get(assignmentStoreId);
		const fromName = this.stationsData.get(fromStationId).name;
		const toName = this.stationsData.get(toStationId).name;

		const newErrorNotification = createAssignmentErrorNotification(fromName, toName, errors);

		notificationStore.setNotification(newErrorNotification);
	}

	setSelectedTimezone = (timeZone) => {
		this.selectedTimezone = timeZone;
	};

	createBatchControls = () => {
		const emptyAssignments = Array.from(this.workAssignments.values()).filter(
			(workAssignment) => workAssignment !== undefined && workAssignment?.crewMember === null
		);
		const batchControls = [];
		for (let i = 0; i < emptyAssignments.length; i++) {
			if (emptyAssignments[i] === undefined) continue;
			const nextAssignmentPair = emptyAssignments.find(
				(assignment) => assignment.fromStationId === emptyAssignments[i].toStationId
			);
			if (nextAssignmentPair === undefined) continue;
			batchControls.push({
				stationId: nextAssignmentPair.fromStationId,
				storeIdPrevious: emptyAssignments[i].storeId,
				storeIdNext: nextAssignmentPair.storeId,
			});
		}
		return batchControls;
	};

	setBatchSelectionOn = (batchData) => {
		runInAction(() => {
			this.workAssignments.set(batchData.storeIdPrevious, {
				...this.workAssignments.get(batchData.storeIdPrevious),
				isPreparedToBatch: true,
			});
			this.workAssignments.set(batchData.storeIdNext, {
				...this.workAssignments.get(batchData.storeIdNext),
				isPreparedToBatch: true,
			});
		});
	};

	setBatchSelectionOff = (batchData) => {
		runInAction(() => {
			this.workAssignments.set(batchData.storeIdPrevious, {
				...this.workAssignments.get(batchData.storeIdPrevious),
				isPreparedToBatch: false,
			});
			this.workAssignments.set(batchData.storeIdNext, {
				...this.workAssignments.get(batchData.storeIdNext),
				isPreparedToBatch: false,
			});
		});
	};

	createBatch = (batchData) => {
		const { storeIdNext, storeIdPrevious } = batchData;

		const prevAssignment = this.workAssignments.get(storeIdPrevious);
		const nextAssigment = this.workAssignments.get(storeIdNext);

		const assignmentToMutate = { ...prevAssignment };
		const { toStationId: nextToStationId, arrival: nextArrival } = nextAssigment;

		runInAction(() => {
			this.workAssignments.set(storeIdPrevious, {
				...assignmentToMutate,
				toStationId: nextToStationId,
				arrival: nextArrival,
				isPreparedToBatch: false,
				isBatchedInUI: true,
				stations: getStationsForAssignment(
					assignmentToMutate.fromStation,
					nextToStationId,
					this.stations
				),
			});
			this.workAssignments.delete(storeIdNext);
		});
	};

	createUnbatchControls = () => {
		const segmentsUiBatched = [...this.workAssignments.values()].filter(
			(workAssignment) => workAssignment !== undefined && workAssignment?.isBatchedInUI === true
		);

		if (segmentsUiBatched.length === 0) return [];
		const preparedControls = segmentsUiBatched.map((segment) => ({
			storeId: segment.storeId,
			stations: getStationsForAssignment(segment.fromStationId, segment.toStationId, this.stations),
		}));
		const flatControls = [];
		preparedControls.forEach((control) => {
			const { storeId, stations } = control;
			const stationsMutated = [...stations];
			stationsMutated.pop();
			stationsMutated.shift();
			stationsMutated.forEach((stationId) => {
				flatControls.push({
					storeId,
					stationId,
				});
			});
		});
		return flatControls;
	};

	breakBatchByStation = (payload) => {
		const { storeId, stationId } = payload;
		const batchedAssignment = this.workAssignments.get(storeId);

		const stationsFirstChunk = getStationsForAssignment(
			batchedAssignment.fromStationId,
			stationId,
			this.stations
		);
		const stationsSecondChunk = getStationsForAssignment(
			stationId,
			batchedAssignment.toStationId,
			this.stations
		);

		const fromStationFirst = stationsFirstChunk[0];
		const toStationFirst = stationsFirstChunk[stationsFirstChunk.length - 1];

		// A cut breaks the global batch: neither chunk spans the full ride
		// anymore, so `isGlobalBatched` no longer holds. Clearing it here
		// also flips the ribbon back from "Ungroup all" to "Group all".
		const prevAssignment = {
			...batchedAssignment,
			fromStationId: fromStationFirst,
			toStationId: toStationFirst,
			storeId: fromStationFirst,
			departure: this.stationsData.get(fromStationFirst).departure,
			arrival: this.stationsData.get(toStationFirst).arrival,
			isGlobalBatched: false,
		};

		const fromStationSecond = stationsSecondChunk[0];
		const toStationSecond = stationsSecondChunk[stationsSecondChunk.length - 1];

		const nextAssigment = {
			...batchedAssignment,
			fromStationId: fromStationSecond,
			toStationId: toStationSecond,
			storeId: fromStationSecond,
			departure: this.stationsData.get(fromStationSecond).departure,
			arrival: this.stationsData.get(toStationSecond).arrival,
			isGlobalBatched: false,
		};

		runInAction(() => {
			this.workAssignments.delete(batchedAssignment.storeId);
			this.workAssignments.set(prevAssignment.storeId, prevAssignment);
			this.workAssignments.set(nextAssigment.storeId, nextAssigment);
		});
	};

	breakRejectedBatch = (storeId) => {
		const rejectedAssignment = this.getAssignmentByID(storeId);

		if (rejectedAssignment.status !== 'rejected') return;
		const newAssignments = breakFullBatch(rejectedAssignment, this.stations);

		runInAction(() => {
			this.workAssignments.delete(storeId);
			newAssignments.forEach((blankAssignment) => {
				const { storeId, fromStationId, toStationId } = blankAssignment;
				this.workAssignments.set(storeId, {
					...blankAssignment,
					departure: this.getStationById(fromStationId).departure,
					arrival: this.getStationById(toStationId).arrival,
				});
			});
		});
	};

	checkAssignAll = () => {
		if (this.isInDriverSelection) return false;
		let couldBeAssignedAll = true;
		const assignmnets = Array.from(this.workAssignments.values());
		assignmnets.forEach((assignmemt) => {
			if (assignmemt.crewMember !== null && assignmemt.status !== null) {
				couldBeAssignedAll = false;
			}
		});
		return couldBeAssignedAll;
	};

	assignAll = () => {
		this._batchAllSegments();
		const key = Array.from(this.workAssignments.keys())[0];
		runInAction(() => {
			this.isInDriverSelection = true;
			this.workAssignmentInSelection = key;
		});
	};

	// Joins every segment into one spanning-the-full-route batch, exactly as
	// assignAll does, but without opening the driver-selection widget.
	// Used by the "Group all" ribbon action.
	groupAll = () => {
		this._batchAllSegments();
	};

	_batchAllSegments = () => {
		const keys = Array.from(this.workAssignments.keys());
		const batchedAll = {
			...this.workAssignments.get(keys[0]),
			storeId: this.stations[0].id,
			fromStationId: this.stations[0].id,
			toStationId: this.stations[this.stations.length - 1].id,
			departure: this.stations[0].departure,
			arrival: this.stations[this.stations.length - 1].arrival,
			isBatchedInUI: true,
			stations: this.stations.map((station) => station.id),
			isGlobalBatched: true,
		};
		runInAction(() => {
			keys.forEach((key) => this.workAssignments.delete(key));
			this.workAssignments.set(batchedAll.fromStationId, batchedAll);
		});
	};

	resetAssignAll = () => {
		this._unbatchAllSegments();
	};

	// Regenerate empty per-station segments from `this.stations`. The
	// segments are a pure FE abstraction over "no crew assigned yet", so
	// there's nothing meaningful to restore — rebuilding a fresh set is
	// equivalent to reverting a prior groupAll/assignAll.
	_unbatchAllSegments = () => {
		const currentBatch = [...this.workAssignments.values()].find((wa) => wa?.isGlobalBatched);
		if (!currentBatch) return;

		const blanks = breakFullBatch(currentBatch, this.stations);
		runInAction(() => {
			this.workAssignments.clear();
			blanks.forEach((blank) => {
				this.workAssignments.set(blank.storeId, {
					...blank,
					departure: this.getStationById(blank.fromStationId).departure,
					arrival: this.getStationById(blank.toStationId).arrival,
				});
			});
			this.isInDriverSelection = false;
			this.workAssignmentInSelection = '';
		});
	};

	disableAutobatchPopup = () => {
		this.showAutobatchPopup = false;
	};

	getDepartureStationCity = () => {
		if (!this.workAssignmentInSelection) return;
		const departureStationId = this.workAssignments.get(
			this.workAssignmentInSelection
		).fromStationId;
		const cityId = this.stationsData.get(departureStationId).city.id;
		return cityId;
	};
}

export const rideDetailsStore = new RideDetailsStore();
